import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { logError } from "utils/logger";

export type SetValue = Dispatch<SetStateAction<string>>;

function useLocalstoragePlaintext(
    key: string,
    initialValue: string
): [string, SetValue, () => void] {
    const readValue = (): string => {
        if (typeof window === "undefined") {
            return initialValue;
        }

        try {
            const item = window.localStorage.getItem(key);
            return item ? item : initialValue;
        } catch (error) {
            console.warn(`Error reading localStorage key “${key}”:`, error);
            return initialValue;
        }
    };

    const [storedValue, setStoredValue] = useState(readValue);

    const setValue: SetValue = (value) => {
        if (typeof window == "undefined") {
            console.warn(
                `Tried setting localStorage key “${key}” even though environment is not a client`
            );
        }

        try {
            // Allow value to be a function so we have same API as useState
            const valueToStore = value instanceof Function ? value(storedValue) : value;

            // Save to local storage
            window.localStorage.setItem(key, valueToStore);

            // Save state
            setStoredValue(valueToStore);

            window.dispatchEvent(new Event("storage"));
        } catch (error) {
            logError("Local storage set value error (setValue)", error);
        }
    };

    const remove = () => {
        if (typeof window == "undefined") {
            console.warn(
                `Tried remove localStorage key “${key}” even though environment is not a client`
            );
        }

        try {
            // Save to local storage
            window.localStorage.removeItem(key);

            // Save state
            setStoredValue("");

            window.dispatchEvent(new Event("storage"));
        } catch (error) {
            logError("Local storage remove value error (remove)", error);
        }
    };

    useEffect(() => {
        setStoredValue(readValue());

        const handleStorageChange = (event: StorageEvent) => {
            if ((event as StorageEvent)?.key && (event as StorageEvent).key !== key) {
                return;
            }

            setStoredValue(readValue());
        };

        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return [storedValue, setValue, remove];
}

export default useLocalstoragePlaintext;
